import { AppointmentPhase } from "components";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import AirlinePaginatedDropdown from "components/dropdowns/paginated/AirlinePaginatedDropdown";
import AirportPaginatedDropdown from "components/dropdowns/paginated/AirportPaginatedDropdown";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ArtistSidePanel } from "components/SidePanel/Company/CompanySidePanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import moment from "moment";
import React from "react";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { FlightDesignatorInput } from "./FlightDesignatorInput";
import { FormatArrivalTime } from "./lookup/FlightSearchResult";
import { FlightInfo } from "./useFlightDetail";

export const flightSections = (
  flight: server.dto.Flight,
  state: FlightInfo,
  getUpdateFlightInstruction: (f: FlightInfo) => server.dto.UpdateFlightInstruction,
): Array<Section> => [{
  key: "flight",
  type: "regular",
  title: flight.flightType === "private" ? RESX.Flight.PrivateFlight : RESX.Flight.resxFlight,
  fields: [{
    key: "artist",
    label: site.currentEnvironment.functionGroups.artist.singular,
    isEmpty: !state.artist,
    render: () => <ArtistSidePanel
      companyId={state.artist.artistId}
      trigger={<a>{state.artist.name}</a>}
      fallbackIfNoPermission={<>{state.artist.name}</>}
    />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
          securityScope="limitByUserAccess"
          canCreateNew={false}
        />
      </div>,
      fullWidth: false,
      type: "custom",
      initialValue: state.artist,
      submitChange: async (artist: server.dto.ArtistSmall) => window.CoreApi.Flight.UpdateFlight(
        getUpdateFlightInstruction({ ...state, artist }),
      ),
    },
  }, {
    key: "status",
    label: RESX.Status.resxStatus,
    render: () => <AppointmentPhase value={state.phase.value} displayLabel />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.AppointmentPhase>) => <AppointmentPhaseDropdown
        value={value}
        onChange={(appointmentPhase) => {
          onChange(appointmentPhase, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.phase,
      submitChange: (phase: server.dto.AppointmentPhase) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, phase }),
      }),
    },
  }, {
    key: "travelAgency",
    label: RESX.Flight.TravelAgency,
    isEmpty: !state.travelAgency,
    render: () => <div>{state.travelAgency}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxTravelAgencyLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.travelAgency ?? "",
      submitChange: (travelAgency: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, travelAgency }),
      }),
    }
  }, {
    key: "notes",
    label: RESX.Note.Notes,
    isEmpty: !state.notes,
    render: () => <OptionalTextAreaDetails>{state.notes}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxNotesLength}
        rows={3}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.notes ?? "",
      submitChange: (notes: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, notes }),
      }),
    },
  }, {
    key: "airline",
    label: RESX.Flight.Airline,
    hidden: flight.flightType !== "commercial",
    render: () => <SmallDetails
      label={state.airline.name}
    />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airline>) => <AirlinePaginatedDropdown
        displayFriendyCodeInSelectedValue={false}
        value={value}
        onChange={(airline) => {
          onChange(airline, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          isSearchable: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.airline,
      submitChange: (airline: server.dto.Airline) => window.CoreApi.Flight.UpdateFlight(
        getUpdateFlightInstruction({ ...state, airline }),
      ),
    }
  }, {
    key: "flightDesignator",
    label: RESX.Flight.FlightNumber,
    hidden: flight.flightType !== "commercial",
    render: () => <div>{state.flightDesignator}</div>,
    inPageEdit: {
      render: ({ value, onChange, cancel, submit }: RenderEditOptions<string>) => <FlightDesignatorInput
        value={value}
        placeholderAirline={state.airline?.iata || state.airline?.icao || ""}
        validatorKey="flightCode"
        onChange={(fd) => onChange(fd)}
        focusOnMount
        onEsc={() => cancel()}
        onEnter={() => submit()}
      />,
      initialValue: state.flightDesignator,
      fullWidth: false,
      type: "confirmOrCancelButton",
      submitChange: (flightDesignator: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, flightDesignator }),
      }),
    }
  }, {
    key: "access",
    label: RESX.SoUser.Access,
    hidden: flight.flightType !== "private",
    isEmpty: !state.access,
    render: () => <OptionalTextAreaDetails>{state.access}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxPrivateJetAccessLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.access ?? "",
      submitChange: (access: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, access }),
      }),
    }
  }, {
    key: "confirmationNumber",
    isEmpty: !state.confirmationNumber,
    label: RESX.Flight.ConfirmationNumber,
    hidden: flight.flightType !== "private",
    render: () => <div>{state.confirmationNumber}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxPrivateJetConfirmationNumberLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.confirmationNumber ?? "",
      submitChange: (confirmationNumber: string) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, confirmationNumber }),
      }),
    }
  }, {
    key: "capacity",
    label: RESX.Venue.Capacity,
    hidden: flight.flightType !== "private",
    isEmpty: !state.capacity,
    render: () => <div>{state.capacity} PAX</div>,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <OptionalNumberInput
        autoFocus
        width={100}
        decimalPoints={0}
        maxValue={1000}
        value={value}
        labelContent="PAX"
        onChange={(v) => { onChange(v); }}
        onEnterPressed={(v) => onChange(v, true)}
        onEscPressed={() => cancel()}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.capacity ?? 0,
      submitChange: (capacity: number) => window.CoreApi.Flight.UpdateFlight({
        ...getUpdateFlightInstruction({ ...state, capacity }),
      }),
    }
  }]
}, {
  key: "departureArrival",
  title: "",
  hideTitle: true,
  type: "regular",
  fields: [{
    key: "departureArrivalLabel",
    label: "",
    left: {
      key: "departureLabel",
      render: () => <h3>{RESX.Flight.Departure}</h3>
    },
    right: {
      key: "arrivalLabel",
      render: () => <h3>{RESX.Flight.Arrival}</h3>
    }
  }, {
    key: "date",
    label: RESX.DateTime.lblDate,
    left: {
      key: "departureLocal",
      render: () => <div className="flex-column">
        <div>{formatTime(state.departureLocal)}</div>
        <div>{formatWrittenDayMonthYear(state.departureLocal)}</div>
      </div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.departureLocal,
        submitChange: (departureLocal: moment.Moment) => {
          const diff = departureLocal.diff(state.departureLocal, "minutes");
          const arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
          return window.CoreApi.Flight.UpdateFlight({
            ...getUpdateFlightInstruction({ ...state, departureLocal, arrivalLocal }),
          })
        }
      }
    },
    right: {
      key: "arrivalLocal",
      render: () => <FormatArrivalTime departure={state.departureLocal} arrival={state.arrivalLocal} />,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="arrivalDate"
          minDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.arrivalLocal,
        submitChange: (arrivalLocal: moment.Moment) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, arrivalLocal }),
        }),
      }
    }
  }, {
    key: "airport",
    label: RESX.Flight.Airport,
    left: {
      key: "departureAirport",
      render: () => <SmallDetails
        label={`${state.departureAirport.name} (${state.departureAirport.iata || state.departureAirport.icao || state.departureAirport.faa})`}
        subLabel={state.departureAirport.location}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airport>) => <AirportPaginatedDropdown
          value={value}
          onChange={(airport) => onChange(airport, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            isSearchable: true,
            openMenuOnFocus: true,
          }}
        />,
        type: "custom",
        initialValue: state.departureAirport,
        fullWidth: true,
        submitChange: (departureAirport: server.dto.Airport) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, departureAirport }),
        }),
      }
    },
    right: {
      key: "arrivalAirport",
      render: () => <SmallDetails
        label={`${state.arrivalAirport.name} (${state.arrivalAirport.iata || state.arrivalAirport.icao || state.arrivalAirport.faa})`}
        subLabel={state.arrivalAirport.location}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airport>) => <AirportPaginatedDropdown
          value={value}
          onChange={(airport) => onChange(airport, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            isSearchable: true,
            openMenuOnFocus: true,
          }}
        />,
        type: "custom",
        initialValue: state.arrivalAirport,
        fullWidth: true,
        submitChange: (arrivalAirport: server.dto.Airport) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, arrivalAirport }),
        }),
      }
    }
  }, {
    key: "terminal",
    label: RESX.Flight.Terminal,
    left: {
      key: "departureTerminal",
      isEmpty: !state.departureTerminal,
      render: () => <div>{state.departureTerminal}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.departureTerminal ?? "",
        submitChange: (departureTerminal: string) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, departureTerminal }),
        }),
      }
    },
    right: {
      key: "arrivalTerminal",
      isEmpty: !state.arrivalTerminal,
      render: () => <div>{state.arrivalTerminal}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.arrivalTerminal ?? "",
        submitChange: (arrivalTerminal: string) => window.CoreApi.Flight.UpdateFlight({
          ...getUpdateFlightInstruction({ ...state, arrivalTerminal }),
        }),
      }
    }
  },

    // , {
    //   key: "distanceAndDuration",
    //   label: "",
    //   hideLabel: true,
    //   hidden: !state.distanceInMeters || !state.durationInMinutes,
    //   render: () => <div className="radius card-floating-bg padding-top-large padding-bottom-large padding-left padding-right">
    //     <div>{RESX.Flight.Distance}:&nbsp;&nbsp;{formatDistance(state.distanceInMeters)}</div>
    //     {state.durationInMinutes && <div>{RESX.Flight.Duration}:&nbsp;&nbsp;{formatTimeDifference(moment.utc(), moment.utc().add(state.durationInMinutes, "minute"))}</div>}
    //   </div>
    // }
  ]
}];